import { Linking } from 'react-native';
import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import { getGetModulesQueryKey } from '@/common/api/endpoints/modules/modules';
import { StudentWorkflowState } from '@/common/api/models/studentWorkflowState';
import type { GenericApiResponseListModuleLecturerResponse } from '@/common/api/models';
import type { AxiosResponse } from 'axios';

const FEEDBACK_DIALOG_DELAY_MS = 100;
/** Give autosubmit a beat before refetch can overwrite optimistic submitted. */
const MODULES_INVALIDATE_DELAY_MS = 3000;

export type CompleteModuleSubmissionUxParams = {
  moduleTitle: string;
  courseId: string;
  moduleId: string;
};

type DialogButton = {
  text: string;
  action?: string;
  testID?: string;
  onPress?: () => void;
};

type ShowDialog = (props: {
  title: string;
  message: string;
  buttons?: DialogButton[];
}) => void;

export type CompleteModuleSubmissionUxDeps = {
  navigation: { navigate: (screen: string, params?: Record<string, unknown>) => void };
  queryClient: QueryClient;
  clearSelection: () => void;
  showToastMessage: (action: 'success' | 'error' | 'warning' | 'info' | 'attention', message: string) => void;
  showDialog: ShowDialog;
  t: TFunction;
};

type ModulesAxiosCache = AxiosResponse<GenericApiResponseListModuleLecturerResponse>;

/**
 * Mark the module submitted in the `/modules` list cache so CourseScreen cannot
 * treat it as workable before autosubmit lands on the server.
 */
export function optimisticallyMarkModuleSubmitted(
  queryClient: QueryClient,
  moduleId: string,
): void {
  queryClient.setQueriesData<ModulesAxiosCache>(
    { queryKey: getGetModulesQueryKey() },
    (old) => {
      if (!old?.data?.data || !Array.isArray(old.data.data)) {
        return old;
      }
      let changed = false;
      const nextModules = old.data.data.map((module) => {
        if (module.id !== moduleId) {
          return module;
        }
        changed = true;
        return {
          ...module,
          state: {
            ...module.state,
            studentWorkflowState: StudentWorkflowState.submitted,
          },
        };
      });
      if (!changed) {
        return old;
      }
      return {
        ...old,
        data: {
          ...old.data,
          data: nextModules,
        },
      };
    },
  );
}

/**
 * Shared post-submit student UX: optimistic submitted state, navigate to
 * CourseScreen, clear selection, success toast, optional feedback dialog.
 * Modules refetch is delayed so oral autosubmit can land before overwrite.
 */
export function completeModuleSubmissionUx(
  deps: CompleteModuleSubmissionUxDeps,
  { moduleTitle, moduleId }: CompleteModuleSubmissionUxParams,
): void {
  optimisticallyMarkModuleSubmitted(deps.queryClient, moduleId);
  deps.navigation.navigate('CourseScreen');
  deps.clearSelection();
  deps.showToastMessage('success', deps.t('student.module.submit-module-dialog.success-message'));

  setTimeout(() => {
    deps.queryClient.invalidateQueries({ queryKey: getGetModulesQueryKey() });
  }, MODULES_INVALIDATE_DELAY_MS);

  setTimeout(() => {
    deps.showDialog({
      title: deps.t('student.module.feedback-dialog.title'),
      message: deps.t('student.module.feedback-dialog.message', { title: moduleTitle }),
      buttons: [
        {
          text: deps.t('student.module.feedback-dialog.button-no'),
          action: 'secondary',
          testID: 'continue-without-feedback',
        },
        {
          text: deps.t('student.module.feedback-dialog.button-yes'),
          testID: 'open-feedback-form',
          onPress: () => {
            const feedbackUrl = process.env.EXPO_PUBLIC_MODULE_FEEDBACK_URL;
            if (feedbackUrl) {
              Linking.openURL(feedbackUrl);
            }
          },
        },
      ],
    });
  }, FEEDBACK_DIALOG_DELAY_MS);
}

export { FEEDBACK_DIALOG_DELAY_MS, MODULES_INVALIDATE_DELAY_MS };
